import {
  GKButton,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
  PlusIcon,
  Show,
} from "components";
import AdvisorLimitModal from "components/AdvisorLimitModal";
import useAddPortfolioModal from "hooks/useAddPortfolioModal";
import { Controller } from "react-hook-form";
import { useAppSelector } from "store/store";
import { GKModalProps } from "utils/commonInterface";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import ClientModal from "./ClientModal";

interface Props {
  showClientButton?: boolean;
}

const AddPortfolioModal = ({
  setOpen,
  open,
  showClientButton = true,
}: Props & GKModalProps) => {
  const clientList = useAppSelector((state) => state?.app?.clientList);
  const partnerList = useAppSelector((state) => state?.app?.partnerList);
  const brokerList = useAppSelector(
    (state) => state?.portfolio?.extraThings?.brokerList,
  );

  const {
    isSubmitting,
    isClientCreateModalOpen,
    setIsClientCreateModalOpen,
    id,
    form,
    onSubmit,
    portfolioReachLimit,
    setPortfolioReachLimit,
  } = useAddPortfolioModal({ setOpen });

  return (
    <>
      <GKModal
        modalTitle="Create Portfolio"
        open={open}
        setOpen={setOpen}
        titleComponent={
          showClientButton && (
            <GKButton
              variant="contained"
              size="medium"
              onClick={() => setIsClientCreateModalOpen(true)}
              startIcon={<PlusIcon width={18} />}
            >
              Add Client
            </GKButton>
          )
        }
        footer={
          <GKButton
            variant="contained"
            type="submit"
            size="medium"
            form="add-portfolio-form"
            loading={isSubmitting}
          >
            Create Portfolio
          </GKButton>
        }
      >
        <>
          <form id="add-portfolio-form" onSubmit={form.handleSubmit(onSubmit)}>
            <Grid2 container spacing={1}>
              <Grid2 size={6}>
                <Controller
                  name="broker"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please select a broker",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKSearchSelect
                      disableClearable
                      inputLabel="Select Broker"
                      requiredField
                      options={brokerList || []}
                      value={
                        brokerList?.find(
                          (dataItem: { value: string }) =>
                            dataItem.value === field.value,
                        )?.name || ""
                      }
                      onChange={(e, val) => field.onChange(val?.value)}
                      getOptionLabel={(option) => option.name || option}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>

              <Show.When isTrue={form.watch("broker") === "OTHER"}>
                <Grid2 size={6}>
                  <Controller
                    name="otherBrokerName"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter broker name",
                      },
                      validate: validateNotWhitespaceOnly,
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <GKTextField
                          {...field}
                          inputLabel="Broker Name"
                          requiredField
                          error={!!error}
                          helperText={error?.message}
                        />
                      );
                    }}
                  />
                </Grid2>
              </Show.When>

              <Grid2 size={6}>
                <Controller
                  name="name"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter name",
                    },
                    validate: validateNotWhitespaceOnly,
                  }}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <GKTextField
                        {...field}
                        inputLabel="Portfolio Name"
                        placeholder="Portfolio Name"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    );
                  }}
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="portfolioId"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter portfolio code",
                    },
                    validate: validateNotWhitespaceOnly,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      requiredField
                      onChange={(event) => {
                        field.onChange(event.target.value.trim());
                      }}
                      inputLabel="Portfolio Code"
                      placeholder="like UCC of Demat A/c"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                {id ? (
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        name={"name"}
                        disabled
                        inputLabel="Client"
                        placeholder="Client"
                        requiredField
                        value={
                          clientList?.find((data) => data?.value === Number(id))
                            ?.name
                        }
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="client"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select a client",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        disableClearable
                        inputLabel="Client"
                        requiredField
                        options={clientList || []}
                        onChange={(e, val) => {
                          field.onChange(val?.value);
                        }}
                        getOptionLabel={(option) => option.name || option}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="partners"
                  control={form.control}
                  render={({ field }) => (
                    <GKSearchSelect
                      disableClearable={false}
                      disableCloseOnSelect
                      inputLabel="Partner"
                      options={partnerList || []}
                      onChange={(e, val) => {
                        field.onChange([val?.value]);
                      }}
                      getOptionLabel={(option) => option.name || option}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </form>

          {isClientCreateModalOpen && (
            <ClientModal
              open={isClientCreateModalOpen}
              setOpen={setIsClientCreateModalOpen}
            />
          )}
        </>
      </GKModal>

      {portfolioReachLimit && (
        <AdvisorLimitModal
          portfolioReachLimit={portfolioReachLimit}
          setPortfolioReachLimit={setPortfolioReachLimit}
        />
      )}
    </>
  );
};

export default AddPortfolioModal;
